import { lazy, Suspense, useEffect, Component } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import SiteLayout from "./components/SiteLayout";
import HomePage from "./pages/HomePage";
import { GA4RouteTracker } from "./analytics/GA4RouteTracker";

const ProductPage = lazy(() => import("./pages/ProductPage"));
const ProductsIndexPage = lazy(() => import("./pages/ProductPage").then((m) => ({ default: m.ProductsIndexPage })));
const ModelPage = lazy(() => import("./pages/ModelPage"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const SolutionsIndexPage = lazy(() => import("./pages/SolutionPage").then((m) => ({ default: m.SolutionsIndexPage })));
const simplePage = (name) => lazy(() => import("./pages/SimplePages").then((m) => ({ default: m[name] })));
const AboutPage = simplePage("AboutPage");
const InstallersLandingPage = simplePage("InstallersLandingPage");
const GalleryPage = simplePage("GalleryPage");
const WarrantyPage = simplePage("WarrantyPage");
const FAQPage = simplePage("FAQPage");
const ContactPage = simplePage("ContactPage");
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const StickerSheet = lazy(() => import("./components/StickerSheet"));
const InstallerPage = lazy(() => import("./components/InstallerPage"));
const CityPage = lazy(() => import("./components/CityPage"));
const AdminPage = lazy(() => import("./components/AdminPage"));

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        if (this.state.failed) {
            return (
                <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-ink px-6 text-center">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">
                        Something went wrong
                    </p>
                    <button
                        data-testid="error-reload-button"
                        onClick={() => window.location.reload()}
                        className="border border-brand/60 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand transition-all duration-300 hover:bg-brand hover:text-ink"
                    >
                        Reload site
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function App() {
    useEffect(() => {
        let lenis;
        let raf;
        let cancelled = false;
        const start = async () => {
            const { default: Lenis } = await import("lenis");
            if (cancelled) return;
            lenis = new Lenis({ duration: 1.15, smoothWheel: true });
            window.__lenis = lenis;
            const loop = (t) => {
                lenis.raf(t);
                raf = requestAnimationFrame(loop);
            };
            raf = requestAnimationFrame(loop);
        };
        const idle = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 1200 }) : setTimeout(start, 400);
        return () => {
            cancelled = true;
            if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
            else clearTimeout(idle);
            cancelAnimationFrame(raf);
            lenis?.destroy();
            delete window.__lenis;
        };
    }, []);

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <GA4RouteTracker />
                <Suspense fallback={<div className="min-h-screen bg-mist" aria-hidden="true" />}>
                <Routes>
                    <Route element={<SiteLayout />}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/products" element={<ProductsIndexPage />} />
                        <Route path="/products/:slug" element={<ProductPage />} />
                        <Route path="/products/:slug/:model" element={<ModelPage />} />
                        <Route path="/solutions" element={<SolutionsIndexPage />} />
                        <Route path="/solutions/:slug" element={<SolutionPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/installers" element={<InstallersLandingPage />} />
                        <Route path="/gallery" element={<GalleryPage />} />
                        <Route path="/warranty" element={<WarrantyPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Route>
                    <Route path="/city/:slug" element={<CityPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/admin/stickers" element={<StickerSheet />} />
                    <Route path="/installer" element={<InstallerPage />} />
                </Routes>
                </Suspense>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
